import * as React from 'react'
import styled, { css } from 'styled-components'

import * as utils from '@owl-nest/utils'
import * as plume from '@ulule/owl-kit-components/next'
import * as entity from '@boutique/entities'

import * as richtext from './richtext'

export type DescriptionProps = {
  description: entity.product.Product['description']
}

export function Description({ description }: DescriptionProps): React.ReactElement<DescriptionProps> {
  const sanitizedDescriptionStyle = utils.text
    .sanitizeDescriptionStyle(description)
    // replace all `hx` in description (SEO) with equally styled divs
    .replace(/<h(\d)/g, (_, level) => {
      return `<div data-bob-h=${level}`
    })

  // FIXME: Set to true if content is initially not long enough
  const [shouldShowAll, setShouldShowAll] = React.useState(
    sanitizedDescriptionStyle.replace(/<[^>]*>?/gm, '').length < 2000,
  )

  return (
    <section>
      <DescriptionWrapper shouldShowAll={shouldShowAll}>
        <DescriptionRichText as="div" dangerouslySetInnerHTML={{ __html: sanitizedDescriptionStyle }} />
      </DescriptionWrapper>
      <ShowMore as="button" hidden={shouldShowAll} onClick={() => setShouldShowAll(true)}>
        Lire plus
      </ShowMore>
    </section>
  )
}

const DescriptionRichText = styled.div`
  a {
    ${richtext._hyperlinkStyle}
  }

  a[style] {
    color: ${plume.COLORS.PRIMARY_BLUE} !important; /* color: color.HYPERLINK; */
  }

  blockquote {
    ${richtext._claimStyle}
  }

  iframe {
    width: 100%;
  }

  img {
    ${richtext._imageStyle}
  }

  li {
    ${richtext._listItemStyle}
    font-size: 16px;
  }

  p {
    ${richtext._paragraphStyle}
    font-size: 16px;
  }

  p[style],
  span[style] {
    color: #13222a !important; /* ds.color.GRAY_3 =>  #13222A */
    font-family: ${plume.FONTS.PRIMARY_FONT} !important; /* ds.font.SECONDARY => sofia pro */
  }

  ol {
    ${richtext._orderedListStyle}
  }

  strong {
    ${richtext._boldStyle}
  }

  ul {
    ${richtext._unorderedListStyle}
  }

  div[data-bob-h='1'] {
    font-size: 32px;
  }
  div[data-bob-h='2'] {
    font-size: 24px;
  }
  div[data-bob-h='3'] {
    font-size: 18.72px;
  }
  div[data-bob-h='4'] {
    font-size: 16px;
  }
  div[data-bob-h='5'] {
    font-size: 13.28px;
  }
  div[data-bob-h='6'] {
    font-size: 10.72px;
  }
`

const DescriptionWrapper = styled.section<{ shouldShowAll: boolean }>`
  max-height: 75vh;
  overflow: hidden;
  position: relative;

  ${({ shouldShowAll }) => {
    if (shouldShowAll) {
      return css`
        height: auto;
        max-height: 100%;
      `
    }

    const gradientBase = plume.utils.hexToRgb(plume.COLORS.PRIMARY_WHITE, 0.1)
    return css`
      &::after {
        background: -webkit-linear-gradient(${gradientBase}, ${plume.COLORS.PRIMARY_WHITE});
        background: linear-gradient(${gradientBase}, ${plume.COLORS.PRIMARY_WHITE});
        content: '';
        height: 11vh;
        left: 0;
        position: absolute;
        top: 65vh;
        width: 100%;
      }
    `
  }}
`

const ShowMoreBase = styled(plume.Link)`
  border: none;
  cursor: pointer;
  margin: 0;
  outline: none;
  padding: 0;

  &:hover,
  &:focus,
  &:active {
    outline: none;
  }
`

const ShowMore = styled(ShowMoreBase)<{ hidden: boolean }>`
  display: block;
  margin-left: auto;
  margin-right: auto;
  margin-top: 15px;
  text-decoration: underline;

  ${({ hidden }) => {
    if (hidden) {
      return css`
        display: none;
      `
    }
  }}

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-right: initial;
    margin-left: initial;
  }
`
